import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut,
  Plus,
  RefreshCw,
  Search,
  X,
  ArrowRight,
  Loader2,
  ChevronDown,
  Download,
} from "lucide-react";
import axios from "axios";
import { zipSync, strToU8 } from "fflate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Resolve the public image URL for a given quote. Prefers the explicit
// `product_image_url` snapshot captured at submission time (new quotes),
// falls back to code→filename guessing for legacy quotes.
// Mirrors the file layout under /app/frontend/public/doors/.
const resolveQuoteImageCandidates = (quote) => {
  const code = quote?.product_code;
  const explicit = quote?.product_image_url;
  const candidates = [];
  if (explicit && typeof explicit === "string") {
    candidates.push(explicit);
  }
  if (!code) return candidates;
  const c = String(code).toLowerCase().trim();
  // Door Configurator submissions carry "DOOR-CFG" — no static image exists
  // (the 3D render is only sent inline in the email, not stored).
  if (c === "door-cfg") return candidates;
  // Steel Doors / Steel Windows / Luxury Entrances have rest/closed/open variants.
  if (/^(sd|sw|le)\d+$/i.test(c)) {
    candidates.push(
      `/doors/${c}-rest.webp`,
      `/doors/${c}-closed.webp`,
      `/doors/${c}-open.webp`,
    );
    return candidates;
  }
  // Hardware (ES-DH*, ES-WH*, ES-LB*, ES-SL*, IV-*) is a single flat image.
  candidates.push(
    `/doors/${c}.webp`,
    `/doors/${c}.jpg`,
    `/doors/${c}.png`,
  );
  return candidates;
};

// Fetches the first candidate URL that returns a valid image. Returns
// { blob, extension } or null when none resolve.
// NOTE: The dev server returns HTML (index.html) with 200 for unknown paths,
// so we must verify the Content-Type is actually an image before trusting it.
const fetchQuoteImage = async (quote) => {
  const candidates = resolveQuoteImageCandidates(quote);
  for (const url of candidates) {
    try {
      const res = await fetch(url, { cache: "force-cache" });
      if (!res.ok) continue;
      const ct = res.headers.get("content-type") || "";
      if (!ct.startsWith("image/")) continue;
      const blob = await res.blob();
      if (!blob || blob.size === 0) continue;
      // Extract extension robustly (handles query strings and absolute URLs).
      const ext = (url.split("?")[0].split(".").pop() || "webp").toLowerCase();
      return { blob, extension: ext };
    } catch {
      // try next candidate
    }
  }
  return null;
};
const STAGES = [
  "Received",
  "In Production",
  "Powder Coating",
  "Ready for Dispatch",
  "Delivered/Installed",
];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("es_admin_token") || ""}`,
});

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-NZ", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [tab, setTab] = useState("orders"); // orders | quotes
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState("");

  const logout = () => {
    localStorage.removeItem("es_admin_token");
    localStorage.removeItem("es_admin_user");
    navigate("/admin/login", { replace: true });
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2800);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, qRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, { headers: authHeaders(), params: q ? { q } : {} }),
        axios.get(`${API}/quotes`, { params: { limit: 100 } }),
      ]);
      setOrders(oRes.data);
      setQuotes(qRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        return;
      }
      showToast("Failed to load. Check connection.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => {
    if (!localStorage.getItem("es_admin_token")) {
      navigate("/admin/login", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("es_admin_user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const filteredQuotes = useMemo(
    () =>
      quotes.filter((qt) =>
        !q
          ? true
          : (qt.email || "").toLowerCase().includes(q.toLowerCase()) ||
            (qt.product_name || "").toLowerCase().includes(q.toLowerCase()) ||
            (qt.product_code || "").toLowerCase().includes(q.toLowerCase())
      ),
    [quotes, q]
  );

  const convertQuote = async (quoteId) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/quotes/${quoteId}/convert`,
        {},
        { headers: authHeaders() }
      );
      showToast(`Order created · ${data.invoice_number}`);
      setTab("orders");
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      showToast(typeof detail === "string" ? detail : "Convert failed.");
    }
  };

  const deleteOrder = async (invoice) => {
    if (!window.confirm(`Delete order ${invoice}? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/orders/${invoice}`, { headers: authHeaders() });
      showToast("Order deleted");
      load();
    } catch {
      showToast("Delete failed.");
    }
  };

  // Builds a plain-text payload matching the "New quote #…" email body format
  // used by the external Eurosteel Quote Machine's text/.eml import.
  const buildQuoteText = (qt) => {
    const ref = (qt.id || "").slice(0, 8).toUpperCase();
    const specLine = (label, value) => (value ? `${label}: ${value}` : null);
    const customColour =
      qt.colour === "Custom" && qt.custom_colour
        ? `Custom · ${qt.custom_colour}`
        : qt.colour;

    const main = [
      specLine("Reference", ref),
      specLine("Submitted", qt.timestamp),
      specLine("Product", qt.product_name),
      specLine("Model", qt.product_code),
      specLine("Customer Email", qt.email),
      specLine("Customer Phone", qt.phone),
      specLine("Application", qt.application),
      specLine("Profile", qt.profile),
      qt.width_mm && qt.height_mm
        ? `Size: ${qt.width_mm} × ${qt.height_mm} mm`
        : null,
      specLine("Quantity", qt.quantity),
      specLine("Opening", qt.opening),
      specLine("Handing", qt.handing),
      specLine("Glass", qt.glass_type),
      specLine("Colour", customColour),
      specLine("Hardware", qt.hardware),
      specLine("Handle Detail", qt.handle_detail),
      specLine("Handle Model", qt.handle_custom_model),
      specLine("Lock", qt.lock),
      specLine("Lock Detail", qt.lock_other),
      specLine("Electronic Lock", qt.electronic_lock),
      specLine("Fixing", qt.fixing),
      specLine("Threshold", qt.threshold),
      specLine("Panel", qt.panel),
    ].filter(Boolean);

    const summary =
      Array.isArray(qt.spec_summary) && qt.spec_summary.length
        ? [
            "",
            "── Configuration ──",
            ...qt.spec_summary.map((r) => `${r.label}: ${r.value}`),
          ]
        : [];

    const notes = qt.notes ? ["", "── Notes ──", qt.notes] : [];

    return [
      `EUROSTEEL — Quote Details`,
      `Quote #${ref}`,
      ...main,
      ...summary,
      ...notes,
      "",
    ].join("\n");
  };

  const downloadQuoteDetails = async (qt) => {
    const text = buildQuoteText(qt);
    const ref = (qt.id || "quote").slice(0, 8).toUpperCase();
    const codeSlug = (qt.product_code || "quote")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-");

    const entries = {
      "quote-details.txt": strToU8(text),
    };

    // Best-effort image attach — if the product has a canonical URL snapshot
    // on the quote (or a static render on disk by code), bundle it alongside
    // the txt so it can be dropped into the quote template.
    const image = await fetchQuoteImage(qt);
    if (image) {
      const buf = new Uint8Array(await image.blob.arrayBuffer());
      // Images are already compressed (webp/jpg/png) — store without re-deflate.
      entries[`door-preview.${image.extension}`] = [buf, { level: 0 }];
    }

    const archive = zipSync(entries, { level: 6 });
    const blob = new Blob([archive], { type: "application/zip" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `eurosteel-quote-${codeSlug}-${ref}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast(
      image
        ? `Downloaded quote #${ref} (incl. image)`
        : `Downloaded quote #${ref}`
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] pt-28 lg:pt-32" data-testid="admin-orders-page">
      {/* Toolbar */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
          <div>
            <p className="font-tech text-[10px] uppercase tracking-[0.4em] text-[#c6a87c]">
              Admin
            </p>
            <h1 className="mt-2 font-serif-display text-3xl text-white lg:text-4xl">
              Orders & Quotes
            </h1>
            <p className="mt-1 font-tech text-[10px] uppercase tracking-[0.28em] text-white/40">
              Signed in as {user.email || "admin"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="flex h-10 items-center gap-2 rounded-full border border-white/15 px-5 font-tech text-[10px] uppercase tracking-[0.24em] text-white/70 transition-colors hover:border-[#c6a87c] hover:text-[#c6a87c]"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={() => setCreating(true)}
              data-testid="admin-new-order-btn"
              className="flex h-10 items-center gap-2 rounded-full bg-[#c6a87c] px-5 font-tech text-[10px] uppercase tracking-[0.24em] text-[#0a0a0c] transition-colors hover:bg-[#d8bd93]"
            >
              <Plus size={13} /> New Order
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex h-10 items-center gap-2 rounded-full border border-white/15 px-5 font-tech text-[10px] uppercase tracking-[0.24em] text-white/70 transition-colors hover:border-red-400/60 hover:text-red-300"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>

        {/* Tabs + Search */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {[
              { id: "orders", label: `Orders (${orders.length})` },
              { id: "quotes", label: `Quote Requests (${quotes.length})` },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`rounded-full px-5 py-2.5 font-tech text-[10px] uppercase tracking-[0.24em] transition-colors ${
                  tab === t.id
                    ? "bg-white/10 text-white"
                    : "text-white/50 hover:text-white/80"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative flex items-center">
            <Search size={14} className="absolute left-4 text-white/40" />
            <input
              data-testid="admin-search-input"
              placeholder="Search invoice, email, product…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="w-72 rounded-full border border-white/15 bg-transparent py-2.5 pl-10 pr-4 font-body text-sm text-white placeholder:text-white/30 focus:border-[#c6a87c] focus:outline-none"
            />
          </div>
        </div>

        {/* Content */}
        <div className="mt-10 pb-24">
          {loading && orders.length === 0 && quotes.length === 0 ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-[#c6a87c]" />
            </div>
          ) : tab === "orders" ? (
            <OrdersTable
              orders={orders}
              onEdit={setEditing}
              onDelete={deleteOrder}
            />
          ) : (
            <QuotesTable
              quotes={filteredQuotes}
              onConvert={convertQuote}
              onDownload={downloadQuoteDetails}
              existingOrders={orders}
            />
          )}
        </div>
      </div>

      {/* Edit modal */}
      <AnimatePresence>
        {editing && (
          <EditOrderModal
            order={editing}
            onClose={() => setEditing(null)}
            onSaved={(u) => {
              setOrders((prev) => prev.map((o) => (o.invoice_number === u.invoice_number ? u : o)));
              setEditing(null);
              showToast(`Updated ${u.invoice_number}`);
            }}
          />
        )}
        {creating && (
          <CreateOrderModal
            onClose={() => setCreating(false)}
            onCreated={() => {
              setCreating(false);
              load();
              showToast("Order created");
            }}
          />
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-8 left-1/2 z-[120] -translate-x-1/2 rounded-full border border-[#c6a87c]/40 bg-[#0a0a0c] px-6 py-3 font-tech text-[10px] uppercase tracking-[0.28em] text-[#c6a87c] shadow-2xl"
            data-testid="admin-toast"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Orders table ────────────────────────────────────────────
const OrdersTable = ({ orders, onEdit, onDelete }) => {
  if (!orders.length)
    return (
      <p className="py-20 text-center font-body text-sm text-white/50">
        No orders yet. Convert a quote or create one to get started.
      </p>
    );

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left" data-testid="admin-orders-table">
        <thead>
          <tr className="border-b border-white/10 text-left font-tech text-[10px] uppercase tracking-[0.28em] text-white/40">
            <th className="py-4 pr-6">Invoice</th>
            <th className="py-4 pr-6">Customer</th>
            <th className="py-4 pr-6">Product</th>
            <th className="py-4 pr-6">Status</th>
            <th className="py-4 pr-6">ETA</th>
            <th className="py-4 pr-6">Updated</th>
            <th className="py-4 pr-6" />
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr
              key={o.invoice_number}
              className="border-b border-white/5 font-body text-sm text-white/80 transition-colors hover:bg-white/[0.02]"
              data-testid={`admin-order-row-${o.invoice_number}`}
            >
              <td className="py-4 pr-6 font-serif-display text-base text-[#c6a87c]">
                {o.invoice_number}
              </td>
              <td className="py-4 pr-6">
                <div className="text-white/90">{o.customer_name || "—"}</div>
                <div className="text-xs text-white/50">{o.customer_email}</div>
              </td>
              <td className="max-w-xs py-4 pr-6 text-white/70">{o.product_summary}</td>
              <td className="py-4 pr-6">
                <span className="rounded-full border border-[#c6a87c]/40 px-3 py-1 text-xs uppercase tracking-[0.16em] text-[#c6a87c]">
                  {o.status}
                </span>
              </td>
              <td className="py-4 pr-6 text-white/70">{o.eta_date || "—"}</td>
              <td className="py-4 pr-6 text-xs text-white/50">{fmtDateTime(o.updated_at)}</td>
              <td className="py-4 pr-6 text-right">
                <button
                  onClick={() => onEdit(o)}
                  data-testid={`admin-edit-${o.invoice_number}`}
                  className="mr-3 font-tech text-[10px] uppercase tracking-[0.24em] text-white/70 hover:text-[#c6a87c]"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(o.invoice_number)}
                  data-testid={`admin-delete-${o.invoice_number}`}
                  className="font-tech text-[10px] uppercase tracking-[0.24em] text-white/40 hover:text-red-300"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Quotes table ────────────────────────────────────────────
const QuotesTable = ({ quotes, onConvert, onDownload, existingOrders }) => {
  const converted = useMemo(
    () => new Set(existingOrders.filter((o) => o.quote_id).map((o) => o.quote_id)),
    [existingOrders]
  );

  if (!quotes.length)
    return <p className="py-20 text-center font-body text-sm text-white/50">No quote requests yet.</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left" data-testid="admin-quotes-table">
        <thead>
          <tr className="border-b border-white/10 font-tech text-[10px] uppercase tracking-[0.28em] text-white/40">
            <th className="py-4 pr-6">Received</th>
            <th className="py-4 pr-6">Email</th>
            <th className="py-4 pr-6">Product</th>
            <th className="py-4 pr-6">Detail</th>
            <th className="py-4 pr-6 text-right" />
          </tr>
        </thead>
        <tbody>
          {quotes.map((qt) => {
            const isConverted = converted.has(qt.id);
            return (
              <tr
                key={qt.id}
                className="border-b border-white/5 font-body text-sm text-white/80"
                data-testid={`admin-quote-row-${qt.id}`}
              >
                <td className="py-4 pr-6 text-xs text-white/50">{fmtDateTime(qt.timestamp)}</td>
                <td className="py-4 pr-6 text-white/90">{qt.email}</td>
                <td className="py-4 pr-6">
                  <div className="text-white/90">{qt.product_name || qt.product_code}</div>
                  <div className="text-xs text-white/50">{qt.product_code}</div>
                </td>
                <td className="max-w-md py-4 pr-6 text-xs text-white/60">
                  {[
                    qt.width_mm && qt.height_mm ? `${qt.width_mm}×${qt.height_mm}mm` : null,
                    qt.colour === "Custom" && qt.custom_colour
                      ? `Custom · ${qt.custom_colour}`
                      : qt.colour,
                    qt.glass_type,
                    qt.quantity ? `Qty ${qt.quantity}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </td>
                <td className="py-4 pr-6 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onDownload(qt)}
                      data-testid={`admin-download-quote-${qt.id}`}
                      title="Download quote (txt + door image) — drag into the Quote Machine"
                      className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-transparent px-3.5 py-2 font-tech text-[10px] uppercase tracking-[0.24em] text-white/80 transition-colors hover:border-[#c6a87c] hover:text-[#c6a87c]"
                    >
                      <Download size={12} /> Download
                    </button>
                    {isConverted ? (
                      <span className="font-tech text-[10px] uppercase tracking-[0.24em] text-white/40">
                        Converted
                      </span>
                    ) : (
                      <button
                        onClick={() => onConvert(qt.id)}
                        data-testid={`admin-convert-${qt.id}`}
                        className="inline-flex items-center gap-2 rounded-full bg-[#c6a87c] px-4 py-2 font-tech text-[10px] uppercase tracking-[0.24em] text-[#0a0a0c] transition-colors hover:bg-[#d8bd93]"
                      >
                        Convert to Order <ArrowRight size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Edit / Create modals ─────────────────────────────────────
const ModalShell = ({ children, onClose, testid }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[110] flex items-center justify-center bg-[#0a0a0c]/85 px-6 py-10 backdrop-blur-md"
    onClick={onClose}
    data-testid={testid}
  >
    <motion.div
      initial={{ y: 30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 20, opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
      onClick={(e) => e.stopPropagation()}
      className="relative w-full max-w-2xl overflow-hidden border border-white/10 bg-[#0a0a0c] p-8 lg:p-10"
    >
      <button
        onClick={onClose}
        className="absolute right-6 top-6 text-white/50 hover:text-white"
        data-testid="modal-close"
      >
        <X size={20} />
      </button>
      {children}
    </motion.div>
  </motion.div>
);

const Field = ({ label, children, testid }) => (
  <label className="block">
    <span className="mb-2 block font-tech text-[10px] uppercase tracking-[0.28em] text-white/50">
      {label}
    </span>
    <div data-testid={testid}>{children}</div>
  </label>
);

const inputCls =
  "w-full border-b border-white/15 bg-transparent py-2.5 font-body text-sm text-white placeholder:text-white/25 focus:border-[#c6a87c] focus:outline-none";

const EditOrderModal = ({ order, onClose, onSaved }) => {
  const [form, setForm] = useState({
    status: order.status,
    eta_date: order.eta_date || "",
    notes: order.notes || "",
    customer_name: order.customer_name || "",
    customer_email: order.customer_email || "",
    product_summary: order.product_summary || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await axios.patch(
        `${API}/admin/orders/${order.invoice_number}`,
        form,
        { headers: authHeaders() }
      );
      onSaved(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} testid="admin-edit-modal">
      <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]">
        {order.invoice_number}
      </p>
      <h2 className="mt-3 font-serif-display text-2xl text-white lg:text-3xl">Update order</h2>

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <Field label="Status" testid="edit-status-wrap">
          <div className="relative">
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className={`${inputCls} appearance-none pr-8`}
              data-testid="edit-status-select"
            >
              {STAGES.map((s) => (
                <option key={s} value={s} className="bg-[#0a0a0c]">
                  {s}
                </option>
              ))}
            </select>
            <ChevronDown
              size={14}
              className="pointer-events-none absolute right-2 top-3 text-white/50"
            />
          </div>
        </Field>
        <Field label="ETA Date">
          <input
            type="date"
            value={form.eta_date}
            onChange={(e) => setForm({ ...form, eta_date: e.target.value })}
            className={inputCls}
            data-testid="edit-eta-input"
          />
        </Field>
        <Field label="Customer Name">
          <input
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            className={inputCls}
          />
        </Field>
        <Field label="Customer Email">
          <input
            type="email"
            value={form.customer_email}
            onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
            className={inputCls}
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Product Summary">
            <input
              value={form.product_summary}
              onChange={(e) => setForm({ ...form, product_summary: e.target.value })}
              className={inputCls}
            />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Notes (visible on tracker)">
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={`${inputCls} resize-none`}
              data-testid="edit-notes-input"
            />
          </Field>
        </div>
      </div>

      {err && (
        <p className="mt-6 border-l-2 border-red-400/60 bg-red-500/5 p-3 font-body text-xs text-red-100/80">
          {err}
        </p>
      )}

      <div className="mt-10 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="rounded-full border border-white/15 px-6 py-3 font-tech text-[10px] uppercase tracking-[0.24em] text-white/70 hover:border-white/40 hover:text-white"
        >
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="edit-save-btn"
          className="flex items-center gap-2 rounded-full bg-[#c6a87c] px-8 py-3 font-tech text-[10px] uppercase tracking-[0.24em] text-[#0a0a0c] hover:bg-[#d8bd93] disabled:opacity-60"
        >
          {saving && <Loader2 size={12} className="animate-spin" />} Save
        </button>
      </div>
    </ModalShell>
  );
};

const CreateOrderModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    customer_email: "",
    customer_name: "",
    product_summary: "",
    eta_date: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setSaving(true);
    setErr("");
    try {
      await axios.post(`${API}/admin/orders`, form, { headers: authHeaders() });
      onCreated();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(" · ")
          : "Create failed."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} testid="admin-create-modal">
      <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]">New order</p>
      <h2 className="mt-3 font-serif-display text-2xl text-white lg:text-3xl">
        Create manual order
      </h2>
      <p className="mt-2 font-body text-sm text-white/50">
        Invoice number is generated automatically once created.
      </p>

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <Field label="Customer Email *">
          <input
            required
            type="email"
            value={form.customer_email}
            onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
            className={inputCls}
            data-testid="create-email-input"
          />
        </Field>
        <Field label="Customer Name">
          <input
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            className={inputCls}
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Product Summary *">
            <input
              required
              value={form.product_summary}
              onChange={(e) => setForm({ ...form, product_summary: e.target.value })}
              placeholder="e.g. SD3 · 2400×1000mm · Black · Double Glazed"
              className={inputCls}
              data-testid="create-product-input"
            />
          </Field>
        </div>
        <Field label="ETA Date">
          <input
            type="date"
            value={form.eta_date}
            onChange={(e) => setForm({ ...form, eta_date: e.target.value })}
            className={inputCls}
          />
        </Field>
        <Field label="Notes">
          <input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className={inputCls}
          />
        </Field>
      </div>

      {err && (
        <p className="mt-6 border-l-2 border-red-400/60 bg-red-500/5 p-3 font-body text-xs text-red-100/80">
          {err}
        </p>
      )}

      <div className="mt-10 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="rounded-full border border-white/15 px-6 py-3 font-tech text-[10px] uppercase tracking-[0.24em] text-white/70 hover:border-white/40 hover:text-white"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={saving || !form.customer_email || !form.product_summary}
          data-testid="create-save-btn"
          className="flex items-center gap-2 rounded-full bg-[#c6a87c] px-8 py-3 font-tech text-[10px] uppercase tracking-[0.24em] text-[#0a0a0c] hover:bg-[#d8bd93] disabled:opacity-60"
        >
          {saving && <Loader2 size={12} className="animate-spin" />} Create
        </button>
      </div>
    </ModalShell>
  );
};
